/** Single source of truth for storage.sync keys (replaces 4× hardcoded lists). */
import {
  DEFAULT_SHORTCUT_BINDINGS,
  type ShortcutBindings,
} from '../shortcuts/bindings.ts';
import type { CustomStylePreset } from '../styles/customStylePresets.ts';
import type { StyleLanguagePriorities } from '../styles/styleLanguagePriorities.ts';
import type { SubtitleOptimizeModel } from '../alignment/subtitleOptimizeModel.ts';

export type { SubtitleOptimizeModel } from '../alignment/subtitleOptimizeModel.ts';
export { SUBTITLE_OPTIMIZE_MODELS } from '../alignment/subtitleOptimizeModel.ts';

export const STORAGE_SCHEMA_VERSION = '24';

export const PRACTICE_MODES = ['watch', 'dictation', 'speaking', 'shadowing', 'learning'] as const;
export type PracticeMode = (typeof PRACTICE_MODES)[number];

export const STORAGE_KEYS = [
  'enable',
  'version',
  'enableShortcuts',
  'shortcutBindings',
  'selectable',
  'subtitleOptimize',
  'subtitleOptimizeModel',
  'subtitleList',
  'darkMode',
  'subY',
  'mainSubY',
  'secondSubY',
  'spacing',
  'maxWidth',
  'translationEngine',
  'mainFontSizeRatio',
  'mainFontWeight',
  'mainFontColor',
  'mainStrokeWidth',
  'mainStrokeColor',
  'mainShadow',
  'mainShadowColor',
  'mainFontBackgroundColor',
  'mainFontBackgroundColorOpacity',
  'mainFontFamily',
  'mainFontGoogleFamilies',
  'mainFontGoogleCssProperties',
  'mainLetterSpacing',
  'mainLineHeight',
  'mainPadding',
  'mainOpacity',
  'mainBoxShadow',
  'secondFontSizeRatio',
  'secondFontWeight',
  'secondFontColor',
  'secondStrokeWidth',
  'secondStrokeColor',
  'secondShadow',
  'secondShadowColor',
  'secondFontBackgroundColor',
  'secondFontBackgroundColorOpacity',
  'secondFontFamily',
  'secondFontGoogleFamilies',
  'secondFontGoogleCssProperties',
  'secondLetterSpacing',
  'secondLineHeight',
  'secondPadding',
  'secondOpacity',
  'secondBoxShadow',
  'mainLanguages',
  'secondLanguages',
  'mainTranslations',
  'secondTranslations',
  'userId',
  'userToken',
  'statusJson',
  'furiganaEnable',
  'furiganaConvertTo',
  'furiganaMode',
  'furiganaRomaji',
  'practiceMode',
  'learningMode',
  'learningModeTargetLanguage',
  'learningModeDefaultPromptId',
  'dictationMode',
  'dictationSubtitle',
  'dictationFilteredWords',
  'dictationIgnoreBracketedText',
  'dictationTypingSoundVolume',
  'dictationInputPosition',
  'speakingMode',
  'speakingPlaybackVolume',
  'shadowingHideUpcoming',
  'shadowingGrayPast',
  'shadowingAutoRemoveLineBreaks',
  'netflixImageBasedSubtitles',
  'subtitleReaderVoice',
  'subtitleReaderRate',
  'subtitleReaderVolume',
  'preProcessing1',
  'preProcessing2',
  'preProcessing3',
  'subtitleSeparation',
  'hoverToReveal',
  'customStylePresets',
  'styleLanguagePriorities',
] as const;

export type StorageKey = (typeof STORAGE_KEYS)[number];

export type Settings = {
  enable: boolean;
  version: string;
  enableShortcuts: boolean;
  shortcutBindings: ShortcutBindings;
  selectable: boolean;
  /**
   * Subtitle optimize (斷句 2/3):
   * - On: show creator-enhanced (YT) / Optimized (NFX/Prime/Coursera) menu rows.
   * - Off (default): those rows are hidden; plain language stays plain.
   * Type 1 ASR Enhanced (YT) is always available and unrelated to this flag.
   * Method 3 never runs silently — user must pick an Optimized/Enhanced row.
   */
  subtitleOptimize: boolean;
  /**
   * AI Segmentation model:
   * - auto (default): member → full (`v`), non-member → trial (`v4`)
   * - small: trial (`v4`) for everyone
   * - large: full (`v`) for members; member-only gate for non-members
   */
  subtitleOptimizeModel: SubtitleOptimizeModel;
  subtitleList: boolean;
  darkMode: boolean;
  subY: number;
  mainSubY: number;
  secondSubY: number;
  spacing: number;
  maxWidth: number;
  translationEngine: string;
  mainFontSizeRatio: number; // default 1 for new installs / unset only — no migration of stored values
  mainFontWeight: number;
  mainFontColor: string;
  mainStrokeWidth: number;
  mainStrokeColor: string;
  mainShadow: string;
  mainShadowColor: string;
  mainFontBackgroundColor: string;
  mainFontBackgroundColorOpacity: number;
  mainFontFamily: string;
  mainFontGoogleFamilies: string;
  mainFontGoogleCssProperties: string;
  mainLetterSpacing: number;
  mainLineHeight: number;
  mainPadding: number;
  mainOpacity: number;
  mainBoxShadow: string;
  secondFontSizeRatio: number;
  secondFontWeight: number;
  secondFontColor: string;
  secondStrokeWidth: number;
  secondStrokeColor: string;
  secondShadow: string;
  secondShadowColor: string;
  secondFontBackgroundColor: string;
  secondFontBackgroundColorOpacity: number;
  secondFontFamily: string;
  secondFontGoogleFamilies: string;
  secondFontGoogleCssProperties: string;
  secondLetterSpacing: number;
  secondLineHeight: number;
  secondPadding: number;
  secondOpacity: number;
  secondBoxShadow: string;
  mainLanguages: string[];
  secondLanguages: string[];
  mainTranslations: string[];
  secondTranslations: string[];
  userId: string;
  userToken: string;
  statusJson: string;
  furiganaEnable: boolean;
  furiganaConvertTo: string;
  furiganaMode: string;
  furiganaRomaji: string;
  practiceMode: PracticeMode;
  learningMode: boolean;
  learningModeTargetLanguage: string;
  learningModeDefaultPromptId: string;
  dictationMode: boolean;
  dictationSubtitle: string;
  dictationFilteredWords: string;
  dictationIgnoreBracketedText: boolean;
  dictationTypingSoundVolume: number;
  dictationInputPosition: string;
  speakingMode: boolean;
  speakingPlaybackVolume: number;
  /** Shadowing: hide text still to the right of the center line. */
  shadowingHideUpcoming: boolean;
  /** Shadowing: gray out text that has crossed the center line. Default on. */
  shadowingGrayPast: boolean;
  /**
   * Shadowing: force 「移除換行」(preProcessing1) while in this mode.
   * Does not change the stored preProcessing1 checkbox. Default on.
   */
  shadowingAutoRemoveLineBreaks: boolean;
  netflixImageBasedSubtitles: boolean;
  subtitleReaderVoice: string;
  subtitleReaderRate: number;
  subtitleReaderVolume: number;
  preProcessing1: boolean;
  preProcessing2: boolean;
  preProcessing3: boolean;
  subtitleSeparation: boolean;
  hoverToReveal: boolean;
  customStylePresets: CustomStylePreset[];
  /** matchKey → custom preset id; priority bindings only (does not alter style bodies). */
  styleLanguagePriorities: StyleLanguagePriorities;
};

/** First-install defaults — mirrors background.js onInstalled (B-EN-01 / B-SET-*). */
export const MAX_WIDTH_MIN = 0.5;
export const MAX_WIDTH_MAX = 0.95;
export const MAX_WIDTH_STEP = 0.05;

export const DEFAULT_SETTINGS: Settings = {
  enable: true,
  version: STORAGE_SCHEMA_VERSION,
  enableShortcuts: true,
  shortcutBindings: { ...DEFAULT_SHORTCUT_BINDINGS },
  selectable: false,
  subtitleOptimize: false,
  subtitleOptimizeModel: 'auto',
  subtitleList: false,
  darkMode: false,
  subY: 0.8,
  mainSubY: 0.8,
  secondSubY: 0.8,
  spacing: 0.005,
  maxWidth: 0.85,
  translationEngine: 'google',
  mainFontSizeRatio: 1,
  mainFontWeight: 500,
  mainFontColor: '#FFFFFF',
  mainStrokeWidth: 0,
  mainStrokeColor: '#000000',
  mainShadow: '',
  mainShadowColor: '#000000',
  mainFontBackgroundColor: '#000000',
  mainFontBackgroundColorOpacity: 0.5,
  mainFontFamily: 'Netflix Sans,Helvetica Neue,Segoe UI,Roboto,Ubuntu,sans-serif',
  mainFontGoogleFamilies: '',
  mainFontGoogleCssProperties: '',
  mainLetterSpacing: 0,
  mainLineHeight: 1.35,
  mainPadding: 8,
  mainOpacity: 1,
  mainBoxShadow: '',
  secondFontSizeRatio: 1,
  secondFontWeight: 500,
  secondFontColor: '#FFFFFF',
  secondStrokeWidth: 0,
  secondStrokeColor: '#000000',
  secondShadow: '',
  secondShadowColor: '#000000',
  secondFontBackgroundColor: '#000000',
  secondFontBackgroundColorOpacity: 0.5,
  secondFontFamily: 'Netflix Sans,Helvetica Neue,Segoe UI,Roboto,Ubuntu,sans-serif',
  secondFontGoogleFamilies: '',
  secondFontGoogleCssProperties: '',
  secondLetterSpacing: 0,
  secondLineHeight: 1.35,
  secondPadding: 8,
  secondOpacity: 1,
  secondBoxShadow: '',
  mainLanguages: [],  secondLanguages: [],
  mainTranslations: [],
  secondTranslations: [],
  userId: '',
  userToken: '',
  statusJson: '',
  furiganaEnable: false,
  furiganaConvertTo: 'hiragana',
  furiganaMode: 'furigana',
  furiganaRomaji: 'nippon',
  practiceMode: 'watch',
  learningMode: false,
  learningModeTargetLanguage: '',
  learningModeDefaultPromptId: '2',
  dictationMode: false,
  dictationSubtitle: 'main',
  dictationFilteredWords: 'oh,uh,um,ooh,whoa,huh,umm,hmm,mm,eh,aww,aw,uhh,ah,mmm,ugh,ow,oop,oops,hm,argh,phew',
  dictationIgnoreBracketedText: false,
  dictationTypingSoundVolume: 0.4,
  dictationInputPosition: 'top',
  speakingMode: false,
  speakingPlaybackVolume: 0.8,
  shadowingHideUpcoming: false,
  shadowingGrayPast: true,
  shadowingAutoRemoveLineBreaks: true,
  netflixImageBasedSubtitles: false,
  subtitleReaderVoice: '',
  subtitleReaderRate: 2,
  subtitleReaderVolume: 0.7,
  preProcessing1: true,
  preProcessing2: false,
  preProcessing3: true,
  subtitleSeparation: false,
  hoverToReveal: false,
  customStylePresets: [],
  styleLanguagePriorities: {},
};

/** Keys read by content.js — mirrors legacy content.js storageKeys exactly. */
export const CONTENT_STORAGE_KEYS = [
  'enableShortcuts',
  'shortcutBindings',
  'selectable',
  'subtitleOptimize',
  'subtitleOptimizeModel',
  'subtitleList',
  'subY',
  'mainSubY',
  'secondSubY',
  'spacing',
  'maxWidth',
  'translationEngine',
  'mainFontSizeRatio',
  'mainFontWeight',
  'mainFontColor',
  'mainStrokeWidth',
  'mainStrokeColor',
  'mainShadow',
  'mainShadowColor',
  'mainFontBackgroundColor',
  'mainFontBackgroundColorOpacity',
  'mainFontFamily',
  'mainFontGoogleFamilies',
  'mainFontGoogleCssProperties',
  'mainLetterSpacing',
  'mainLineHeight',
  'mainPadding',
  'mainOpacity',
  'mainBoxShadow',
  'secondFontSizeRatio',
  'secondFontWeight',
  'secondFontColor',
  'secondStrokeWidth',
  'secondStrokeColor',
  'secondShadow',
  'secondShadowColor',
  'secondFontBackgroundColor',
  'secondFontBackgroundColorOpacity',
  'secondFontFamily',
  'secondFontGoogleFamilies',
  'secondFontGoogleCssProperties',
  'secondLetterSpacing',
  'secondLineHeight',
  'secondPadding',
  'secondOpacity',
  'secondBoxShadow',
  'mainLanguages',
  'secondLanguages',
  'mainTranslations',
  'secondTranslations',
  'userId',
  'userToken',
  'statusJson',
  'furiganaEnable',
  'furiganaConvertTo',
  'furiganaMode',
  'furiganaRomaji',
  'practiceMode',
  'learningMode',
  'learningModeTargetLanguage',
  'learningModeDefaultPromptId',
  'dictationMode',
  'dictationSubtitle',
  'dictationFilteredWords',
  'dictationIgnoreBracketedText',
  'dictationTypingSoundVolume',
  'dictationInputPosition',
  'speakingMode',
  'speakingPlaybackVolume',
  'shadowingHideUpcoming',
  'shadowingGrayPast',
  'shadowingAutoRemoveLineBreaks',
  'netflixImageBasedSubtitles',
  'subtitleReaderVoice',
  'subtitleReaderRate',
  'subtitleReaderVolume',
  'preProcessing1',
  'preProcessing2',
  'preProcessing3',
  'subtitleSeparation',
  'hoverToReveal',
  'customStylePresets',
  'styleLanguagePriorities',
] as const satisfies readonly StorageKey[];

/** Keys read by popup.js — mirrors legacy popup.js storageKeys exactly. */
export const POPUP_STORAGE_KEYS = [
  'enable',
  'enableShortcuts',
  'shortcutBindings',
  'selectable',
  'subtitleOptimize',
  'subtitleOptimizeModel',
  'subtitleList',
  'darkMode',
  'subY',
  'mainSubY',
  'secondSubY',
  'spacing',
  'maxWidth',
  'translationEngine',
  'mainFontSizeRatio',
  'mainFontWeight',
  'mainFontColor',
  'mainStrokeWidth',
  'mainStrokeColor',
  'mainShadow',
  'mainShadowColor',
  'mainFontBackgroundColor',
  'mainFontBackgroundColorOpacity',
  'mainFontFamily',
  'mainFontGoogleFamilies',
  'mainFontGoogleCssProperties',
  'mainLetterSpacing',
  'mainLineHeight',
  'mainPadding',
  'mainOpacity',
  'mainBoxShadow',
  'secondFontSizeRatio',
  'secondFontWeight',
  'secondFontColor',
  'secondStrokeWidth',
  'secondStrokeColor',
  'secondShadow',
  'secondShadowColor',
  'secondFontBackgroundColor',
  'secondFontBackgroundColorOpacity',
  'secondFontFamily',
  'secondFontGoogleFamilies',
  'secondFontGoogleCssProperties',
  'secondLetterSpacing',
  'secondLineHeight',
  'secondPadding',
  'secondOpacity',
  'secondBoxShadow',
  'furiganaEnable',  'furiganaConvertTo',
  'furiganaMode',
  'furiganaRomaji',
  'practiceMode',
  'learningMode',
  'learningModeTargetLanguage',
  'learningModeDefaultPromptId',
  'dictationMode',
  'dictationSubtitle',
  'dictationFilteredWords',
  'dictationIgnoreBracketedText',
  'dictationTypingSoundVolume',
  'dictationInputPosition',
  'speakingMode',
  'speakingPlaybackVolume',
  'shadowingHideUpcoming',
  'shadowingGrayPast',
  'shadowingAutoRemoveLineBreaks',
  'netflixImageBasedSubtitles',
  'subtitleReaderVoice',
  'subtitleReaderRate',
  'subtitleReaderVolume',
  'preProcessing1',
  'preProcessing2',
  'preProcessing3',
  'subtitleSeparation',
  'hoverToReveal',
  'customStylePresets',
  'styleLanguagePriorities',
] as const satisfies readonly StorageKey[];

/** Keys read by content.js (includes membership + language prefs). */
export function isStorageKey(key: string): key is StorageKey {
  return (STORAGE_KEYS as readonly string[]).includes(key);
}

/** Popup reset control restores subY to schema default (B-SET-02). */
export function resetSubY(settings: Partial<Settings>): Partial<Settings> {
  return { ...settings, subY: DEFAULT_SETTINGS.subY };
}

export function isPopupStorageKey(key: string): key is StorageKey {
  return (POPUP_STORAGE_KEYS as readonly string[]).includes(key);
}

/**
 * Popup reset button payload — mirrors popup.js onResetClick (B-SET-02).
 * Preserves legacy quirk: learningModeDefaultPromptId is '0' on reset, not install default '2'.
 */
export function getPopupResetDefaults(): Partial<Settings> {
  const { version: _version, userId: _userId, userToken: _userToken, statusJson: _statusJson, ...defaults } =
    DEFAULT_SETTINGS;
  return {
    ...defaults,
    learningModeDefaultPromptId: '0',
  };
}

export function isPracticeMode(value: unknown): value is PracticeMode {
  return typeof value === 'string' && (PRACTICE_MODES as readonly string[]).includes(value);
}

/** Missing / invalid practiceMode → watch. Legacy `dubbing` → `shadowing`. */
export function normalizePracticeMode(value: unknown): PracticeMode {
  if (value === 'dubbing') return 'shadowing';
  return isPracticeMode(value) ? value : 'watch';
}

/** Derive legacy feature flags from the exclusive practiceMode. */
export function syncPracticeFlags(practiceMode: PracticeMode): Pick<
  Settings,
  'practiceMode' | 'learningMode' | 'dictationMode' | 'speakingMode'
> {
  const mode = normalizePracticeMode(practiceMode);
  return {
    practiceMode: mode,
    learningMode: mode === 'learning',
    dictationMode: mode === 'dictation' || mode === 'speaking',
    speakingMode: mode === 'speaking',
  };
}
